import React from 'react';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import IconButton from '@mui/joy/IconButton';
import Button from '@mui/joy/Button';
import Chip from '@mui/joy/Chip';
import CircularProgress from '@mui/joy/CircularProgress';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SyncIcon from '@mui/icons-material/Sync';
import ErrorIcon from '@mui/icons-material/Error';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import ReplayIcon from '@mui/icons-material/Replay';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { themeColors, shadows } from '../../styles/theme';

const headerKeyframes = `
  @keyframes headerFadeIn {
    from { opacity: 0; transform: translateX(8px); }
    to { opacity: 1; transform: translateX(0); }
  }

  @keyframes headerCheckPop {
    0% { transform: scale(0); opacity: 0; }
    60% { transform: scale(1.3); }
    100% { transform: scale(1); opacity: 1; }
  }
`;

export default function ModernFlowHeader({
  project,
  workspace,
  onBack,
  saveStatus,
  lastSaved,
  onTestRunClick,
  testRunStatus,
  onStopTestRun,
  onClearTestRun,
}) {
  const getSaveStatusConfig = () => {
    switch (saveStatus) {
      case 'saving':
        return {
          icon: <SyncIcon sx={{ fontSize: 12, animation: 'spin 1s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />,
          label: 'Saving',
          color: themeColors.primary[600],
          bgColor: `${themeColors.primary[500]}10`,
        };
      case 'saved':
        return {
          icon: <CheckCircleIcon sx={{ fontSize: 12 }} />,
          label: 'Saved',
          color: themeColors.success,
          bgColor: `${themeColors.success}10`,
        };
      case 'error':
        return {
          icon: <ErrorIcon sx={{ fontSize: 12 }} />,
          label: 'Error',
          color: themeColors.danger,
          bgColor: `${themeColors.danger}10`,
        };
      default:
        return {
          icon: <FiberManualRecordIcon sx={{ fontSize: 12 }} />,
          label: 'Draft',
          color: themeColors.neutral[500],
          bgColor: themeColors.neutral[100],
        };
    }
  };

  const statusConfig = getSaveStatusConfig();

  return (
    <>
      <style>{headerKeyframes}</style>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5,
          py: 1.75,
          backgroundColor: 'white',
          borderBottom: `1px solid ${themeColors.neutral[100]}`,
          minHeight: 56,
        }}
      >
        {/* Left Section - Back Button & Project Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
          <IconButton
            onClick={onBack}
            size="sm"
            sx={{
              width: 32,
              height: 32,
              backgroundColor: themeColors.neutral[100],
              border: 'none',
              borderRadius: 1.5,
              flexShrink: 0,
              '&:hover': {
                backgroundColor: themeColors.neutral[200],
              },
              transition: 'all 0.15s ease',
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} />
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            {project?.main_color && (
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: 0.75,
                  backgroundColor: project.main_color,
                  border: `1px solid ${themeColors.neutral[200]}`,
                  flexShrink: 0,
                }}
              />
            )}

            <Box sx={{ minWidth: 0 }}>
              <Typography
                level="body-sm"
                sx={{
                  fontWeight: 600,
                  color: themeColors.neutral[900],
                  fontSize: '0.875rem',
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {project?.name || 'Untitled Flow'}
              </Typography>
              {workspace?.name && (
                <Typography
                  level="body-xs"
                  sx={{
                    color: themeColors.neutral[600],
                    fontSize: '0.6875rem',
                    lineHeight: 1.2,
                  }}
                >
                  {workspace.name}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* Right Section - Test Run + Save Status */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
          {/* Open Client Button */}
          {(project?.workspace_url_name || workspace?.url_name) && (
            <Button
              variant="plain"
              size="sm"
              startDecorator={<OpenInNewIcon sx={{ fontSize: 15 }} />}
              onClick={() => window.open(`/client/${project?.workspace_url_name || workspace?.url_name}`, '_blank')}
              sx={{
                fontWeight: 600,
                fontSize: '0.8125rem',
                height: 32,
                px: 1.5,
                backgroundColor: themeColors.neutral[100],
                color: themeColors.neutral[700],
                border: 'none',
                borderRadius: 1.5,
                whiteSpace: 'nowrap',
                transition: 'all 0.15s ease',
                '&:hover': {
                  backgroundColor: `${themeColors.primary[500]}10`,
                  color: themeColors.primary[600],
                },
              }}
            >
              Client
            </Button>
          )}

          {/* Separator */}
          {(project?.workspace_url_name || workspace?.url_name) && (
            <Box sx={{ width: '1px', height: 16, backgroundColor: themeColors.neutral[200] }} />
          )}

          {/* Test Run Controls */}
          {(!testRunStatus || testRunStatus === 'idle') && (
            <Button
              variant="plain"
              size="sm"
              startDecorator={<PlayArrowIcon sx={{ fontSize: 15 }} />}
              onClick={onTestRunClick}
              sx={{
                fontWeight: 600,
                fontSize: '0.8125rem',
                height: 32,
                px: 1.5,
                backgroundColor: themeColors.neutral[100],
                color: themeColors.neutral[700],
                border: 'none',
                borderRadius: 1.5,
                whiteSpace: 'nowrap',
                transition: 'all 0.15s ease',
                '&:hover': {
                  backgroundColor: `${themeColors.primary[500]}10`,
                  color: themeColors.primary[600],
                },
              }}
            >
              Test Run
            </Button>
          )}

          {testRunStatus === 'running' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, animation: 'headerFadeIn 0.25s ease-out' }}>
              <Chip
                startDecorator={
                  <CircularProgress
                    size="sm"
                    sx={{
                      '--CircularProgress-size': '14px',
                      '--CircularProgress-trackThickness': '2px',
                      '--CircularProgress-progressThickness': '2px',
                    }}
                  />
                }
                size="sm"
                sx={{
                  backgroundColor: `${themeColors.primary[500]}10`,
                  color: themeColors.primary[700],
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  border: 'none',
                  height: 28,
                  px: 1.5,
                  whiteSpace: 'nowrap',
                }}
              >
                Running...
              </Chip>
              <IconButton
                size="sm"
                onClick={onStopTestRun}
                sx={{
                  width: 28,
                  height: 28,
                  backgroundColor: `${themeColors.danger}10`,
                  border: 'none',
                  borderRadius: 1,
                  color: themeColors.danger,
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    backgroundColor: `${themeColors.danger}18`,
                    transform: 'scale(1.05)',
                  },
                }}
              >
                <StopIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          )}

          {testRunStatus === 'completed' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, animation: 'headerFadeIn 0.25s ease-out' }}>
              <Chip
                startDecorator={<CheckCircleIcon sx={{ fontSize: 14, animation: 'headerCheckPop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)' }} />}
                size="sm"
                sx={{
                  backgroundColor: `${themeColors.success}10`,
                  color: themeColors.success,
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  border: 'none',
                  height: 28,
                  px: 1.5,
                  whiteSpace: 'nowrap',
                }}
              >
                Complete
              </Chip>
              <IconButton
                size="sm"
                onClick={onTestRunClick}
                sx={{
                  width: 28,
                  height: 28,
                  border: 'none',
                  borderRadius: 1,
                  color: themeColors.primary[500],
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    backgroundColor: `${themeColors.primary[500]}10`,
                    color: themeColors.primary[700],
                  },
                }}
              >
                <ReplayIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton
                size="sm"
                onClick={onClearTestRun}
                sx={{
                  width: 28,
                  height: 28,
                  border: 'none',
                  borderRadius: 1,
                  color: themeColors.neutral[400],
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    backgroundColor: themeColors.neutral[100],
                    color: themeColors.neutral[600],
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          )}

          {testRunStatus === 'error' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, animation: 'headerFadeIn 0.25s ease-out' }}>
              <Chip
                startDecorator={<ErrorIcon sx={{ fontSize: 14 }} />}
                size="sm"
                sx={{
                  backgroundColor: `${themeColors.danger}10`,
                  color: themeColors.danger,
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  border: 'none',
                  height: 28,
                  px: 1.5,
                  whiteSpace: 'nowrap',
                }}
              >
                Error
              </Chip>
              <IconButton
                size="sm"
                onClick={onTestRunClick}
                sx={{
                  width: 28,
                  height: 28,
                  border: 'none',
                  borderRadius: 1,
                  color: themeColors.primary[500],
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    backgroundColor: `${themeColors.primary[500]}10`,
                    color: themeColors.primary[700],
                  },
                }}
              >
                <ReplayIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton
                size="sm"
                onClick={onClearTestRun}
                sx={{
                  width: 28,
                  height: 28,
                  border: 'none',
                  borderRadius: 1,
                  color: themeColors.neutral[400],
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    backgroundColor: themeColors.neutral[100],
                    color: themeColors.neutral[600],
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          )}

          {/* Separator */}
          {testRunStatus && testRunStatus !== 'idle' && (
            <Box sx={{ width: '1px', height: 16, backgroundColor: themeColors.neutral[200], mx: 0.25 }} />
          )}

          {lastSaved && saveStatus === 'saved' && (
            <Typography
              level="body-xs"
              sx={{
                color: themeColors.neutral[500],
                fontSize: '0.6875rem',
                whiteSpace: 'nowrap',
              }}
            >
              {new Date(lastSaved).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </Typography>
          )}

          <Chip
            startDecorator={statusConfig.icon}
            size="sm"
            sx={{
              backgroundColor: statusConfig.bgColor,
              color: statusConfig.color,
              fontWeight: 600,
              fontSize: '0.6875rem',
              border: 'none',
              height: 24,
              px: 1.5,
              whiteSpace: 'nowrap',
            }}
          >
            {statusConfig.label}
          </Chip>
        </Box>
      </Box>
    </>
  );
}
